import type { Action } from 'vuex'
import type {
  RecipientOptionList,
  RecipientOption,
} from '@/api/send_cart/model/sendCartModel'
import type { State } from '.'
import type { UpdateRecipientMasterItem } from '@/api/recipient_master/model/recipientMaster'
import type { RecipientMasterCreationFormData } from '@/hooks/services/recipient-master/useRecipientMaster'
import type { FolderItem } from '@/api/model/folderModel'

import actionTypes from './actionTypes'
import { getRecipientOptions } from '@/api/send_cart/send_cart'
import { SubPageEnum, ThirdPageEnum } from './state'
import { formatDate } from '@/utils'
import {
  queryFolderMasterList,
  updateRecipientMasterList,
} from '@/api/recipient_master/recipientMaster'

const actions: { [actionTypes: string]: Action<State, any> } = {
  [actionTypes.SET_ACTIVE_SUB_PAGE]({ commit }, page: SubPageEnum | null): void {
    commit(actionTypes.SET_ACTIVE_SUB_PAGE, page)
  },

  [actionTypes.SET_ACTIVE_THIRD_PAGE]({ commit }, page: ThirdPageEnum): void {
    commit(actionTypes.SET_ACTIVE_THIRD_PAGE, page)
  },

  async [actionTypes.INITIALIZE_RECIPIENT_MASTER_OPTIONS]({ commit }): Promise<void> {
    const data: RecipientOptionList = await getRecipientOptions()

    data.forEach((item: RecipientOption) => {
      item.insert_datetime = formatDate(item.insert_datetime)
      item.update_datetime = formatDate(item.update_datetime)
    })

    commit(actionTypes.INITIALIZE_RECIPIENT_MASTER_OPTIONS, data)
  },

  [actionTypes.SELECT_RECIPIENT_MASTER]({ commit }, recipient: RecipientOption): void {
    commit(actionTypes.SELECT_RECIPIENT_MASTER, recipient)
  },

  [actionTypes.CHANGE_RECIPIENT_MASTER_NAME](
    { commit, state },
    recipient: RecipientOption
  ) {
    // 編集時にソートを崩さない為に一覧内のアイテムの名前のみ変更する
    const newList: RecipientOptionList =
      state.recipientMasterOptions.map<RecipientOption>((item: RecipientOption) => {
        if (item.recipient_id !== recipient.recipient_id) {
          return { ...item }
        }

        return { ...item, recipient_name: recipient.recipient_name }
      })

    commit(actionTypes.CHANGE_RECIPIENT_MASTER_NAME, newList)
  },

  [actionTypes.DELETE_RECIPIENT_MASTER](
    { commit, state },
    target: RecipientOption
  ): void {
    const newList: RecipientOptionList = state.recipientMasterOptions.filter(
      (item: RecipientOption) => item.recipient_id !== target.recipient_id
    )

    commit(actionTypes.DELETE_RECIPIENT_MASTER, newList)
  },

  [actionTypes.CREATE_RECIPIENT_MASTER](
    { commit, state },
    data: RecipientMasterCreationFormData
  ): void {
    const folder = state.folderList.find(
      (_folder: FolderItem) => _folder.folder_id === data.folderID
    ) as FolderItem

    const newRecipientMaster: RecipientOption = {
      corporate_id: null as unknown as number,
      // 送信前に編集する際の特定用、送信時は null に変更すべき
      recipient_id: Date.now(),
      recipient_name: data.recipientName,
      folder_id: folder.folder_id,
      folder_name: folder.forder_name,
      insert_datetime: '',
      insert_user_name: '',
      update_datetime: '',
    }

    commit(actionTypes.CREATE_RECIPIENT_MASTER, newRecipientMaster)
  },

  async [actionTypes.UPDATE_RECIPIENT_MASTER_LIST]({ state }): Promise<void> {
    // 編集した送信先マスタ
    const editedList: RecipientOptionList = state.recipientMasterOptions.filter(
      (item: RecipientOption): boolean => {
        const backup = state.recipientMasterOptionsBackup.find(
          (backupItem: RecipientOption): boolean =>
            backupItem.recipient_id === item.recipient_id
        )

        // バックアップに無いデータは新規作成のデータ
        if (!backup) {
          return false
        }

        return item.recipient_name !== backup.recipient_name
      }
    )

    const formattedEditedList = editedList.map<UpdateRecipientMasterItem>(
      (item: RecipientOption) => ({
        recipient_id: item.recipient_id,
        recipient_name: item.recipient_name,
        folder_id: item.folder_id,
        delete_flag: '0',
      })
    )

    // 削除した送信先マスタ
    const deletedList: RecipientOptionList = state.recipientMasterOptionsBackup.filter(
      (backupItem: RecipientOption): boolean => {
        return !state.recipientMasterOptions
          .map<number>((item: RecipientOption) => item.recipient_id)
          .includes(backupItem.recipient_id)
      }
    )

    const formattedDeletedList = deletedList.map<UpdateRecipientMasterItem>(
      (item: RecipientOption) => ({
        recipient_id: item.recipient_id,
        recipient_name: item.recipient_name,
        folder_id: item.folder_id,
        delete_flag: '1',
      })
    )

    // 作成した送信先マスタ
    const formattedCreatedList = state.recipientMasterOptions
      .filter((item: RecipientOption): boolean => !item.corporate_id)
      .map<UpdateRecipientMasterItem>((item: RecipientOption) => ({
        recipient_id: null as unknown as number,
        recipient_name: item.recipient_name,
        folder_id: item.folder_id,
        delete_flag: '0',
      }))

    const editList: UpdateRecipientMasterItem[] = [
      ...formattedEditedList,
      ...formattedDeletedList,
      ...formattedCreatedList,
    ]

    await updateRecipientMasterList({ recipient_list: editList })
  },

  async [actionTypes.SET_FOLDER_LIST]({ commit }): Promise<void> {
    const list = await queryFolderMasterList()
    commit(actionTypes.SET_FOLDER_LIST, list)
  },

  //登録確認ダイアログ
  [actionTypes.SET_CORPORATE_NUMBER_ADD_SUBMIT_MODAL]({ commit }, status: boolean): void {
    commit(actionTypes.SET_CORPORATE_NUMBER_ADD_SUBMIT_MODAL, status)
  },

  //新規登録フラグ
  [actionTypes.SET_SUBMIT_FLAG]({ commit }, flag: string): void {
    commit(actionTypes.SET_SUBMIT_FLAG, flag)
  },

  //新規登録の法人番号
  [actionTypes.SET_NEW_CORPORATE_NUMBER]: ({ commit }, newNumber: string): void => {
    commit(actionTypes.SET_NEW_CORPORATE_NUMBER, newNumber)
  },

  //新規登録の法人名
  [actionTypes.SET_NEW_CORPORATE_NAME]: ({ commit }, newName: string): void => {
    commit(actionTypes.SET_NEW_CORPORATE_NAME, newName)
  },

  //送信先詳細id
  [actionTypes.SET_RECIPIENT_DETAIL_ID]: ({ commit }, id: number): void => {
    commit(actionTypes.SET_RECIPIENT_DETAIL_ID, id)
  },

  //選択された法人番号、法人名
  [actionTypes.SET_SELECTED_CORPORATE_NUMBER]: ({ commit }, corporateNumber: number): void => {
    commit(actionTypes.SET_SELECTED_CORPORATE_NUMBER, corporateNumber)
  },

  [actionTypes.SET_SELECTED_CORPORATE_NAME]: ({ commit }, corporateName: string): void => {
    commit(actionTypes.SET_SELECTED_CORPORATE_NAME, corporateName)
  },

  //初取引先コードか否か
  [actionTypes.SET_IS_FIRST_CLIENT]: ({ commit }, status: boolean): void => {
    commit(actionTypes.SET_IS_FIRST_CLIENT, status)
  },

  //新規登録の取引先コード
  [actionTypes.SET_NEW_CLIENT_CODE]: ({ commit }, newCode: string): void => {
    commit(actionTypes.SET_NEW_CLIENT_CODE, newCode)
  },

  //新規登録の取引先名
  [actionTypes.SET_NEW_CLIENT_NAME]: ({ commit }, newName: string): void => {
    commit(actionTypes.SET_NEW_CLIENT_NAME, newName)
  },

  //選択された取引先
  [actionTypes.SET_SELECTED_CLIENT_CODE]: ({ commit }, newCode: string): void => {
    commit(actionTypes.SET_SELECTED_CLIENT_CODE, newCode)
  },

  [actionTypes.SET_SELECTED_CLIENT_NAME]: ({ commit }, newName: string): void => {
    commit(actionTypes.SET_SELECTED_CLIENT_NAME, newName)
  },

  //新規登録の担当者名
  [actionTypes.SET_NEW_RECIPIENT_USER_NAME]: ({ commit }, newName: string): void => {
    commit(actionTypes.SET_NEW_RECIPIENT_USER_NAME, newName)
  },

  //新規登録の送信先組織
  [actionTypes.SET_NEW_RECIPIENT_DEPARTMENT_NAME]: ({ commit }, newName: string): void => {
    commit(actionTypes.SET_NEW_RECIPIENT_DEPARTMENT_NAME, newName)
  },

  //新規登録のメールアドレス
  [actionTypes.SET_NEW_RECIPIENT_MAIL]: ({ commit }, newMail: string): void => {
    commit(actionTypes.SET_NEW_RECIPIENT_MAIL, newMail)
  },
}

export default actions
